/**
 * CollectorSummaryTable — Panel izquierdo de la conciliacion
 * Tabla resumen de 12 meses (filas) x 4 columnas de status (OK, Solo CC, Solo CR, Descuadre)
 * Estilo visual alineado con DB Conciliacion
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText, Database,
  ArrowLeftRight, HelpCircle, CheckCircle, XCircle, AlertTriangle, X,
} from 'lucide-react';
import {
  type ColumnTooltip,
  type SummaryColumn,
  SUMMARY_COLUMNS,
  MONTH_NAMES,
} from '@/types/collector-reconciliation.types';

// ─── Color maps ──────────────────────────────────────────────────────────────

const CELL_COLOR: Record<string, string> = {
  green: 'text-green-400',
  red: 'text-red-400',
  amber: 'text-amber-400',
  rose: 'text-rose-400',
  slate: 'text-slate-400',
};

const CELL_HOVER: Record<string, string> = {
  green: 'hover:bg-green-500/20',
  red: 'hover:bg-red-500/20',
  amber: 'hover:bg-amber-500/20',
  rose: 'hover:bg-rose-500/20',
  slate: 'hover:bg-slate-500/20',
};

const CELL_SELECTED: Record<string, string> = {
  green: 'bg-green-500/30 ring-1 ring-green-500',
  red: 'bg-red-500/30 ring-1 ring-red-500',
  amber: 'bg-amber-500/30 ring-1 ring-amber-500',
  rose: 'bg-rose-500/30 ring-1 ring-rose-500',
  slate: 'bg-slate-500/30 ring-1 ring-slate-500',
};

type ColorKey = 'green' | 'red' | 'amber' | 'rose' | 'slate';

const HEADER_ICON: Record<ColorKey, typeof CheckCircle> = {
  green: CheckCircle,
  red: XCircle,
  amber: AlertTriangle,
  rose: AlertTriangle,
  slate: HelpCircle,
};

const TOOLTIP_TITLE_COLOR: Record<ColorKey, string> = {
  green: 'text-green-400',
  red: 'text-red-400',
  amber: 'text-amber-400',
  rose: 'text-rose-400',
  slate: 'text-slate-400',
};

// ─── Rich Tooltip Card (rendered via portal) ─────────────────────────────────

interface TooltipCardProps {
  tooltip: ColumnTooltip;
  color: ColorKey;
  anchorRect: DOMRect;
  onClose: () => void;
}

function TooltipCard({ tooltip, color, anchorRect, onClose }: TooltipCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Position: below the anchor, aligned right
  const top = anchorRect.bottom + 6;
  const right = window.innerWidth - anchorRect.right;

  const TitleIcon = HEADER_ICON[color];
  const titleColor = TOOLTIP_TITLE_COLOR[color];

  return createPortal(
    <div
      ref={cardRef}
      className="fixed z-50 w-72 rounded-lg border border-border bg-card shadow-xl"
      style={{ top, right: Math.max(8, right - 40) }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2">
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${titleColor}`}>
          <TitleIcon size={14} />
          {tooltip.title}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X size={12} />
        </button>
      </div>

      {/* Description */}
      <p className="px-3 pb-2 text-[11px] leading-relaxed text-foreground/80">
        {tooltip.description}
      </p>

      {/* Relation */}
      {tooltip.relation && (
        <p className="px-3 pb-2 text-[11px]">
          <span className="text-cyan-400">Relacion: </span>
          <span className="font-mono text-muted-foreground">{tooltip.relation}</span>
        </p>
      )}

      {/* Conditions */}
      {tooltip.conditions?.map((cond) => (
        <div key={cond.label} className="px-3 pb-2">
          <p className={`text-[11px] font-semibold ${cond.severity === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
            {cond.severity === 'ok' ? '✓ ' : '🚨 '}{cond.label}:
          </p>
          <ul className="ml-3 mt-0.5 list-disc text-[11px] text-muted-foreground">
            {cond.items.map((item) => (
              <li key={item} className={cond.severity === 'error' ? 'text-red-400/80' : ''}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* Note */}
      {tooltip.note && (
        <p className="border-t border-border/50 px-3 py-2 text-[10px] text-muted-foreground">
          {tooltip.note}
        </p>
      )}
    </div>,
    document.body,
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  monthData: Record<string, Record<string, unknown>> | null;
  onCellClick: (month: number, statusKey: string) => void;
  selectedMonth: number | null;
  selectedStatus: string | null;
  /** Override columns (default: SUMMARY_COLUMNS from collector types) */
  columns?: SummaryColumn[];
  /** Override header content (default: CC ↔ CR) */
  headerContent?: React.ReactNode;
  /** Override legend items (default: CC statuses). Pass null to hide. */
  legendItems?: { label: string; color: string }[] | null;
}

export function CollectorSummaryTable({
  monthData,
  onCellClick,
  selectedMonth,
  selectedStatus,
  columns: columnsProp,
  headerContent,
  legendItems,
}: Props) {
  const columns = columnsProp ?? SUMMARY_COLUMNS;
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);

  const handleTooltipToggle = useCallback((key: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (openTooltip === key) {
      setOpenTooltip(null);
    } else {
      setTooltipRect(e.currentTarget.getBoundingClientRect());
      setOpenTooltip(key);
    }
  }, [openTooltip]);

  const getMonthValue = (month: number, key: string): number => {
    const m = monthData?.[String(month)];
    if (!m) return 0;
    return (m as unknown as Record<string, number>)[key] ?? 0;
  };

  const monthHasData = (month: number): boolean => {
    return columns.some((col) => getMonthValue(month, col.key) > 0);
  };

  const getTotals = (key: string): number => {
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      total += getMonthValue(m, key);
    }
    return total;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        {headerContent ?? (
          <>
            <Database size={14} className="text-purple-400" />
            <ArrowLeftRight size={10} className="text-muted-foreground" />
            <FileText size={14} className="text-teal-400" />
            <span className="ml-1.5 text-xs font-medium text-blue-400">CreditContract ↔ CollectionRecord</span>
          </>
        )}
      </div>

      {/* Tabla */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Mes</th>
              {columns.map((col) => {
                const Icon = HEADER_ICON[col.color];
                return (
                  <th key={col.key} className="px-3 py-2 text-center text-xs font-medium">
                    <span className={`flex items-center justify-center gap-1 ${CELL_COLOR[col.color]}`}>
                      <Icon size={12} />
                      {col.label}
                      <button
                        type="button"
                        onClick={(e) => handleTooltipToggle(col.key, e)}
                        className="rounded p-0.5 opacity-40 transition-opacity hover:opacity-100"
                        aria-label={`Info: ${col.label}`}
                      >
                        <HelpCircle size={11} />
                      </button>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
              const hasData = monthHasData(month);
              return (
                <tr key={month} className="border-b border-border/50">
                  <td className="px-3 py-2 text-xs font-medium text-foreground">
                    {MONTH_NAMES[month - 1]}
                  </td>
                  {columns.map((col) => {
                    const value = getMonthValue(month, col.key);
                    const isSelected = selectedMonth === month && selectedStatus === col.key;

                    if (!hasData) {
                      return (
                        <td key={col.key} className="px-2 py-2 text-center">
                          <span className="text-muted-foreground/40">-</span>
                        </td>
                      );
                    }

                    if (value > 0) {
                      return (
                        <td key={col.key} className="px-2 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => onCellClick(month, col.key)}
                            className={`rounded px-2 py-0.5 text-xs font-semibold tabular-nums transition-colors ${
                              CELL_COLOR[col.color]
                            } ${CELL_HOVER[col.color]} ${
                              isSelected ? CELL_SELECTED[col.color] : ''
                            }`}
                          >
                            {value}
                          </button>
                        </td>
                      );
                    }

                    return (
                      <td key={col.key} className="px-2 py-2 text-center">
                        <span className={col.color === 'green' ? 'text-muted-foreground/40' : 'text-green-400/50'}>
                          0
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sticky bottom-0 border-t-2 border-border bg-card font-medium">
              <td className="px-3 py-2 text-xs font-bold text-foreground">TOTAL</td>
              {columns.map((col) => {
                const total = getTotals(col.key);
                return (
                  <td key={col.key} className="px-3 py-2 text-center">
                    {total > 0 ? (
                      <span className={`text-xs font-bold tabular-nums ${CELL_COLOR[col.color]}`}>
                        {total}
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-red-400/60">-</span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Leyenda de estados */}
      {legendItems !== null && (
        <div className="mt-3 flex flex-wrap gap-3 text-[10px]">
          {(legendItems ?? [
            { label: 'ORIGINADO', color: 'bg-amber-400' },
            { label: 'COMPRADO', color: 'bg-purple-400' },
            { label: 'COBRADO', color: 'bg-blue-400' },
            { label: 'SALDADO', color: 'bg-emerald-400' },
          ]).map((item) => (
            <span key={item.label} className="flex items-center gap-1">
              <span className={`inline-block h-2 w-2 rounded-full ${item.color}`} />
              {item.label}
            </span>
          ))}
        </div>
      )}

      {/* Tooltip card (portal) */}
      {openTooltip && tooltipRect && (() => {
        const col = columns.find((c) => c.key === openTooltip);
        if (!col) return null;
        return (
          <TooltipCard
            tooltip={col.tooltip}
            color={col.color}
            anchorRect={tooltipRect}
            onClose={() => setOpenTooltip(null)}
          />
        );
      })()}
    </div>
  );
}
